export const AUTH_REDIRECT_QUERY_PARAM = 'next';
export const AUTH_REDIRECT_FALLBACK_PATH = '/';

export const AUTH_ROUTE_PREFIXES = ['/login', '/signup'];
export const PROTECTED_ROUTE_PREFIXES = ['/settings', '/my-list', '/my-progress'];

const AUTH_REDIRECT_BASE_URL = 'https://trakio.local';

function normalizePathname(pathname: string): string {
  if (!pathname) return AUTH_REDIRECT_FALLBACK_PATH;

  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

function matchesRoutePrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some(prefix => {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}

export function getPathnameWithoutLocale(
  pathname: string,
  supportedLocales: readonly string[]
): string {
  const normalizedPathname = normalizePathname(pathname);
  const [, maybeLocale] = normalizedPathname.split('/');

  if (maybeLocale && supportedLocales.includes(maybeLocale)) {
    return normalizedPathname.slice(maybeLocale.length + 1) || '/';
  }

  return normalizedPathname;
}

export function getLocaleFromPathname(
  pathname: string,
  supportedLocales: readonly string[]
): string | null {
  const normalizedPathname = normalizePathname(pathname);
  const [, maybeLocale] = normalizedPathname.split('/');

  if (maybeLocale && supportedLocales.includes(maybeLocale)) {
    return maybeLocale;
  }

  return null;
}

export function isProtectedRoutePathname(
  pathname: string,
  supportedLocales: readonly string[]
): boolean {
  return matchesRoutePrefix(
    getPathnameWithoutLocale(pathname, supportedLocales),
    PROTECTED_ROUTE_PREFIXES
  );
}

export function isAuthRoutePathname(
  pathname: string,
  supportedLocales: readonly string[]
): boolean {
  return matchesRoutePrefix(
    getPathnameWithoutLocale(pathname, supportedLocales),
    AUTH_ROUTE_PREFIXES
  );
}

export function buildPathWithSearch(
  pathname: string,
  search?: string | null,
  hash?: string | null
): string {
  const normalizedSearch = search
    ? search.startsWith('?')
      ? search
      : `?${search}`
    : '';
  const normalizedHash = hash ? (hash.startsWith('#') ? hash : `#${hash}`) : '';

  return `${normalizePathname(pathname)}${normalizedSearch}${normalizedHash}`;
}

export function sanitizeAuthRedirectTarget(
  target: string | null | undefined,
  supportedLocales: readonly string[]
): string {
  if (!target) return AUTH_REDIRECT_FALLBACK_PATH;

  const trimmedTarget = target.trim();

  if (
    !trimmedTarget.startsWith('/') ||
    trimmedTarget.startsWith('//') ||
    trimmedTarget.includes('\\')
  ) {
    return AUTH_REDIRECT_FALLBACK_PATH;
  }

  try {
    const parsedTarget = new URL(trimmedTarget, AUTH_REDIRECT_BASE_URL);

    if (parsedTarget.origin !== AUTH_REDIRECT_BASE_URL) {
      return AUTH_REDIRECT_FALLBACK_PATH;
    }

    if (isAuthRoutePathname(parsedTarget.pathname, supportedLocales)) {
      return AUTH_REDIRECT_FALLBACK_PATH;
    }

    return `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`;
  } catch {
    return AUTH_REDIRECT_FALLBACK_PATH;
  }
}

export function getLocalizedAuthPathname(
  currentPathname: string,
  authPathname: string,
  supportedLocales: readonly string[]
): string {
  const locale = getLocaleFromPathname(currentPathname, supportedLocales);

  if (!locale) return authPathname;

  return `/${locale}${authPathname}`;
}

export function buildAuthRedirectPath(
  authPathname: string,
  redirectTarget: string,
  supportedLocales: readonly string[]
): string {
  const safeRedirectTarget = sanitizeAuthRedirectTarget(
    redirectTarget,
    supportedLocales
  );
  const searchParams = new URLSearchParams();

  searchParams.set(AUTH_REDIRECT_QUERY_PARAM, safeRedirectTarget);

  return `${authPathname}?${searchParams.toString()}`;
}

export function buildAuthPageLinkPath(
  authPathname: string,
  redirectTarget: string,
  shouldPreserveRedirectTarget: boolean
): string {
  if (!shouldPreserveRedirectTarget) return authPathname;

  const searchParams = new URLSearchParams();

  searchParams.set(AUTH_REDIRECT_QUERY_PARAM, redirectTarget);

  return `${authPathname}?${searchParams.toString()}`;
}
